import PropTypes from 'prop-types';
import { BsStars, BsX } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import { useErrorStore } from '#lib/stores/errorStore';

import { useDismissSuggestion } from '../../../models/insights/mutations';
import ChannelBadge from '../../common/ChannelBadge/ChannelBadge';

/**
 * Renders one crmAiSuggestions row with a dismiss action.
 */

function formatDueDate(iso) {
	if (!iso) return null;
	try {
		const d = new Date(iso);
		return new Intl.DateTimeFormat('de-DE', {
			day: '2-digit',
			month: 'short',
			hour: '2-digit',
			minute: '2-digit',
		}).format(d);
	} catch {
		return null;
	}
}

const AiSuggestionCard = ({ suggestion, showDealTitle = false, onDismissed }) => {
	const dismissMutation = useDismissSuggestion();
	const showError = useErrorStore((s) => s.showError);

	const dueLabel = formatDueDate(suggestion.dueAt);

	const handleDismiss = async () => {
		try {
			await dismissMutation.mutateAsync(suggestion.id);
			onDismissed?.(suggestion);
		} catch (err) {
			showError({ error: err, title: 'Konnte nicht verwerfen' });
		}
	};

	return (
		<div className="rounded-card border border-primary-200 bg-primary-100 p-3 hover:border-primary-300 transition-colors">
			<div className="flex items-start gap-2">
				<BsStars className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-2 flex-wrap">
						<span className="text-[11px] font-semibold uppercase tracking-wide text-primary-700">
							KI-Vorschlag
						</span>
						{suggestion.aiScore !== null && (
							<span className="text-[10px] text-bgBase-800">Score {suggestion.aiScore}</span>
						)}
					</div>
					<p className="text-sm font-medium text-text mt-0.5">{suggestion.title}</p>
					{suggestion.rationale && (
						<p className="text-xs text-text mt-1 whitespace-pre-wrap">{suggestion.rationale}</p>
					)}
					<div className="flex items-center gap-2 mt-2 flex-wrap">
						{suggestion.channelTypeId && (
							<ChannelBadge channelTypeId={suggestion.channelTypeId} size="xs" />
						)}
						{dueLabel && <span className="text-[11px] text-bgBase-800">Fällig: {dueLabel}</span>}
						{showDealTitle && suggestion.dealTitle && (
							<span className="text-[11px] text-bgBase-800 truncate">· {suggestion.dealTitle}</span>
						)}
					</div>
				</div>
				<div className="flex flex-col gap-1 flex-shrink-0">
					<Button variant="outline" size="sm" onClick={handleDismiss} loading={dismissMutation.isPending}>
						<BsX className="w-3 h-3 mr-1" />
						Verwerfen
					</Button>
				</div>
			</div>
		</div>
	);
};

AiSuggestionCard.propTypes = {
	suggestion: PropTypes.object.isRequired,
	showDealTitle: PropTypes.bool,
	onDismissed: PropTypes.func,
};

export default AiSuggestionCard;
